import styled, { keyframes } from 'styled-components';
import { colors } from '~/styles/remoteControlColors';
import { Variant } from './ModalTemplate';

export const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

export const ModalContainer = styled.div<{ padding: number, variant?: Variant, fullScreenMobile?: boolean }>`
  background: white;
  width: ${({ variant }) => (variant === 'large' ? '600' : '420')}px;
  border-radius: 8px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  padding: ${({ padding }) => padding}px;
  display: flex;
  gap: 32px;
  flex-direction: column;

  @media (max-width: 768px) {
    margin-top: auto;
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
    ${({ variant }) => (variant === 'large' ? 'width: 100%;' : '')}
    ${({ fullScreenMobile, padding }) => (fullScreenMobile ? `
      height: 100dvh;
      padding: 0;
      padding-bottom: ${padding}px;
    ` : '')}
  }
`;

export const ModalHeader = styled.div<{ fullScreenMobile?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  font-size: 20px;

  ${({ fullScreenMobile }) => (fullScreenMobile ? `
    img {
      display: none;
    }

    @media (max-width: 768px) {
      height: 84px;
      padding: 24px;
      border-bottom: 1px solid ${colors.Grey300};

      img {
        display: block;
        margin: 0 auto;
      }

      button {
        margin: auto 0;
      }
    }
  ` : '')}

`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  align-self: flex-start;
  display: flex;
  padding: 6px;
`;

export const ModalContent = styled.p`
  color: ${colors.Grey400};
  font-size: 16px;
  margin-bottom: 30px;
  text-align: center;
`;

export const ButtonContainer = styled.div<{ fullScreenMobile?: boolean, hide?: boolean }>`
  display: ${({ hide }) => (hide ? 'none' : 'flex')};
  flex-direction: column;
  gap: 10px;

  ${({ fullScreenMobile }) => (fullScreenMobile ? `
    @media (max-width: 768px) {
      padding: 0 24px;
      margin-top: auto;
    }
  ` : '')}
`;

export const Button = styled.button<{ primary?: boolean, disabled?: boolean }>`
  padding: 12px;
  font-size: 18px;
  border-radius: 8px;
  font-weight: 500;
  font-family: Inter, sans-serif;
  cursor: pointer;
  border: none;
  text-align: center;
  background: ${({ primary }) => (primary ? colors.Blue : colors.Grey200)};
  color: ${({ primary }) => (primary ? colors.White : colors.Grey400)};

  &:hover {
    opacity: 0.9;
  }

  ${({ disabled }) => disabled
    && `
      background: ${colors.Soft200};
      color: ${colors.Grey400};
      cursor: not-allowed;
      pointer-events: none;
      &:hover {
        opacity: 1;
      }
    `}
`;

export const BoxEconomyMode = styled.div`
  width: 100%;
  background-color: ${colors.White};
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.15);
  height: 56px;
  border-radius: 8px;
  cursor: pointer;

  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  margin-top: 20px;
  color: ${colors.Black};
  div {
    display: flex;
    gap: 8px;
  }
`;

export const BoxEconomyModeOff = styled.div`
  width: 100%;
  background-color: ${colors.Grey200};
  display: flex;
  justify-content: space-between;
  padding: 20px;
  font-size: 14px;
  margin-top: 40px;
  text-align: left;
  position: relative;
  border-radius: 10px;
  line-height: 130%;
  color: ${colors.Grey400};
  div {
    display: flex;
    width: 80%;
  }
  svg {
    position: absolute;
    top: -14px;
    right: 12px;
  }
  @media (max-width: 385px) {
    div {
      display: flex;
      width: 70%;
    }
    svg {
      position: absolute;
      top: 23px;
      right: 12px;
    }
  }
`;

export const CloseButtonName = styled.button`
  color: ${colors.Blue};
  border: none;
  background: none;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  padding: 12px;
`;

export const BoxOpeningHours = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${colors.Black};
  font-size: 16px;
  font-weight: 500;
  margin-top: 20px;
  gap: 16px;
`;

export const BoxSendingInfoMachineError = styled.div`
  color: ${colors.Black};
  font-size: 16px;
  font-weight: 500;
  line-height: 100%;
  div {
    margin-top: 15px;
  }
`;

export const BoxSendingInfoMachine = styled.div`
  color: ${colors.Black100};
  font-size: 17px;
  font-weight: 500;
  line-height: 100%;
  margin-top: 0px;
  div {
    color: ${colors.Grey};
    font-size: 12px;
    font-weight: 400;
    line-height: 180%;
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: center;
    margin-top: 20px;
    letter-spacing: 0%;
    font-family: Inter, sans-serif;
  }
`;

const fadeInOut = keyframes`
  0% { opacity: 0; transform: translateY(-10px); }
  10% { opacity: 1; transform: translateY(0); }
  90% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-10px); }
`;

const colorsType = {
  warning: '#d97706',
  error: '#c53030',
  success: '#2f855a',
};

const backgroundType = {
  warning: '#fffaf0',
  error: '#fff5f5',
  success: '#f0fff4',
};

export const PopupContainer = styled.div<{ type: 'success' | 'error' | 'warning' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #f0fff4;
  color: #2f855a;
  padding: 12px 16px;
  border-radius: 8px;
  position: fixed;
  bottom: 20px;
  left: 53%;
  z-index: 1000;
  transform: translateX(-50%);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  font-weight: 600;
  color: ${({ type }) => (colorsType[type])};
  background-color: ${({ type }) => (backgroundType[type])};
`;

export const DisconnectedDetailsContainer = styled.div`
  margin-top: 8px;

  ol li {
    margin-bottom: 8px;
  }

  ol li::marker {
    font-weight: 500;
    font-size: 16px;
    color: ${colors.Grey500};
  }

  h3 {
    font-weight: 500;
    font-size: 16px;
    color: ${colors.Grey500};
  }

  span {
    font-weight: 500;
    font-size: 12px;
    color: ${colors.Grey400};
    margin-left: -18px;
  }
`;
